/**
 * @import { MapProps } from '@vis.gl/react-google-maps'
 * @import { MapRegion } from '@/types/map-region'
 */

export const mapControlClassName = 'vikingMapControl';

export const defaultMapOptions = {
  defaultBounds: {
    east: 76,
    west: -56,
    south: -45,
    north: 70,
  },
  minZoom: 2,
  disableDefaultUI: true,
  clickableIcons: false,
};

/**
 * @param {Partial<MapProps>} [props]
 * @returns {MapProps}
 */
export function getDefaultMapProps(props) {
  return {
    ...defaultMapOptions,
    ...props,
    mapId: props?.mapId || 'DEMO_MAP_ID',
  };
}

/**
 * @param {number} zoomLevel
 * @return {number}
 */
export function degreesPerPixel(zoomLevel) {
  // 360° divided by the number of pixels at the zoom-level
  return 360 / (Math.pow(2, zoomLevel) * 256);
}

/**
 * Returns a zoom level to fit the provided bounds for a Google Map
 *
 * See:  {@link https://stackoverflow.com/a/13274361}
 * @param {google.maps.LatLngBounds} bounds
 * @param {Element} mapEl
 * @returns {number}
 */
export function getBoundsZoomLevel(bounds, mapEl) {
  const mapDim = { height: mapEl.clientHeight, width: mapEl.clientWidth };
  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 21;

  /**
   * @param {number} lat
   * @returns {number}
   */
  function latRad(lat) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  /**
   * @param {number} mapPx
   * @param {number} worldPx
   * @param {number} fraction
   * @returns {number}
   */
  function zoom(mapPx, worldPx, fraction) {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  }

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

  const lngDiff = ne.lng() - sw.lng();
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

  const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, ZOOM_MAX);
}

/**
 * @param {google.maps.Map | null} map
 * @param {Array<{position: [number, number]}>} places
 */
export function fitBoundsToPlaces(map, places) {
  if (!map) return;

  const extent = new google.maps.LatLngBounds();
  for (const place of places) {
    extent.extend({ lng: place.position[0], lat: place.position[1] });
  }

  if (!extent.isEmpty()) {
    map.fitBounds(extent, 60);
  }
}

/**
 * @param {google.maps.Map | null} map
 * @param {MapRegion | null} region
 */
export function fitBoundsToRegion(map, region) {
  if (!map || !region) return;

  const { west, south, east, north } = region.bounds;
  const extent = new google.maps.LatLngBounds({ lng: west, lat: south }, { lng: east, lat: north });

  if (!extent.isEmpty()) {
    map.fitBounds(extent, region.padding ?? 60);
  }
}

/**
 * @param {google.maps.Map} map
 */
export function setViewToRegion(map) {
  map.setZoom(defaultMapOptions.minZoom);
  
  // Center of Europe
  map.setCenter({ lng: 25.8, lat: 54.9 });
}
